import { useQuery, QueryKey } from "react-query";
import { getResultSummariesBulk } from "pages/api-clients/graph-search";
import { DeepLinkType, DigestV2State } from "pages/api/deep-links/types";
import { isExploreCard } from "pages/api/deep-links/types";
import { TopicCard } from "../cards/types";
import { getDateRangeOrDuration } from "pages/[orgId]/teams/utils/date-range";
import { useRouter } from "next/router";
import { mergeFilters } from "pages/[orgId]/boards/lib";

interface UseTopicResultsOptions {
  // Cache control
  cacheKey?: string;
  cacheTime?: number;
  staleTime?: number;
  // Query dependencies for cache invalidation
  dependencies?: unknown[];
}

interface UseTopicResultsProps {
  orgId: string;
  state?: DigestV2State;
  prompt: string;
  options?: UseTopicResultsOptions;
}

const MAX_RESULTS = 1_000_000;

export function useTopicResults({
  orgId,
  state,
  prompt,
  options = {},
}: UseTopicResultsProps) {
  const router = useRouter();

  // Parse filters from query param if present
  let queryFilters: any[] = [];
  if (typeof router.query.filters === "string") {
    try {
      queryFilters = JSON.parse(router.query.filters);
    } catch {}
  }

  // Merge defaultFilters and queryFilters
  const mergedFilters = mergeFilters(state?.defaultFilters || [], queryFilters);

  // Build a stable query key that includes required dependencies
  const queryKey: QueryKey = [
    "topic-results",
    options.cacheKey || "default",
    orgId,
    prompt,
    {
      prompt: prompt,
      dateRange: state?.defaultDateRange,
      dataKeys: state?.data ? Object.keys(state.data).sort() : [],
      filters: mergedFilters,
      ...(options.dependencies ? { deps: options.dependencies } : {}),
    },
  ];

  return useQuery(
    queryKey,
    async () => {
      try {
        if (prompt && state?.data) {
          return await getResultSummariesBulk(
            {
              sections: toSections(state, prompt, mergedFilters),
            },
            orgId,
            prompt
          );
        }
        return null;
      } catch (error) {
        console.error(
          "Error in useTopicResults:",
          JSON.stringify(error, null, 2)
        );

        throw error;
      }
    },
    {
      enabled: Boolean(state?.data && prompt.length > 0),
      retry: false,
      staleTime: options.staleTime ?? 5000,
      cacheTime: options.cacheTime ?? 300_000, // 5 minutes default
      onError: (error) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("API error", error, state?.name, state?.status);
        }
      },
    }
  );
}

function toSections(state: DigestV2State, prompt: string, filters: any[] = []) {
  return [
    {
      prompt,
      queries: Object.values(state.data)
        .filter(isExploreCard)
        .map((card) => ({
          ...card.state.params,
          type: DeepLinkType.EXPLORE,
          filters: mergeFilters(card.state.params.filters || [], filters),
          ...getDateRangeOrDuration(
            state.defaultDateRange?.startDate && state.defaultDateRange?.endDate
              ? [
                  state.defaultDateRange.startDate,
                  state.defaultDateRange.endDate,
                ]
              : undefined,
            state.defaultDateRange?.duration || "P7D"
          ),
        })),
    },
  ];
}
